import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  UserCheck, 
  Fingerprint, 
  Scan, 
  Bell, 
  Smartphone, 
  Shield, 
  Calendar,
  CheckCircle,
  Users,
  MessageSquare,
  BookOpen,
  Clock,
  CreditCard,
  FileText,
  GraduationCap,
  Library,
  Bus,
  ChartBar,
  ClipboardList,
  Settings,
  Check,
  Globe,
  Phone,
  Sparkles
} from 'lucide-react';

// ... (previous code remains the same until the SchoolManagement section)

const ProductShowcase = () => {
  // ... (previous arrays and content remain the same)
  const navigate = useNavigate();
  const packages = [
    {
      title: "Essential Bundle",
      description: "Perfect starter pack for schools",
      icon: <Shield className="w-12 h-12 text-yellow-500" />,
      mainFeatures: [
        "RFID Attendance System",
        "School Management Software",
        "School Website",
        "Student Mobile App (iOS + Android)",
        "Demand Based Features Add-ons"
      ],
      includedFeatures: [
        "Real-time Attendance Tracking",
        "Parent Notifications",
        "Holiday Management",
        "Leave Approval System",
        "Basic Report Generation",
        "24/7 Support"
      ],
      highlight: false
    },
    {
      title: "Premium Bundle",
      description: "Most Popular Choice",
      icon: <Sparkles className="w-12 h-12 text-yellow-500" />,
      mainFeatures: [
        "RFID + Fingerprint System",
        "School Management Software",
        "School Website",
        "Student Mobile App (iOS + Android)",
        "Demand Based Features Add-ons"
      ],
      includedFeatures: [
        "Dual Authentication",
        "Advanced Analytics",
        "Custom Report Generation",
        "Multi-Branch Support",
        "API Integration",
        "Priority Support"
      ],
      highlight: true
    },
    {
      title: "Ultimate Bundle",
      description: "Complete school digitalization",
      icon: <GraduationCap className="w-12 h-12 text-yellow-500" />,
      mainFeatures: [
        "Face + Fingerprint + RFID System",
        "School Management Software",
        "School Website",
        "Student Mobile App (iOS + Android)",
        "Demand Based Features Add-ons"
      ],
      includedFeatures: [
        "Triple Authentication",
        "White-label Solution",
        "Custom Module Development",
        "Dedicated Support Manager",
        "Regular Feature Updates",
        "Data Migration Assistance"
      ],
      highlight: false
    }
  ];

  const AppFeatures = () => (

    
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
      <motion.div
        className="bg-gray-900 p-6 rounded-lg"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <Smartphone className="w-8 h-8 text-yellow-500 mb-4" />
        <h4 className="text-lg font-bold mb-2">Student App</h4>
        <ul className="space-y-2">
          <li className="flex items-center"><Check className="w-4 h-4 text-yellow-500 mr-2" />View Attendance History</li>
          <li className="flex items-center"><Check className="w-4 h-4 text-yellow-500 mr-2" />Class Schedule</li>
          <li className="flex items-center"><Check className="w-4 h-4 text-yellow-500 mr-2" />Homework Updates</li>
        </ul>
      </motion.div>

      <motion.div
        className="bg-gray-900 p-6 rounded-lg"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.1 }}
      >
        <Globe className="w-8 h-8 text-yellow-500 mb-4" />
        <h4 className="text-lg font-bold mb-2">School Website</h4>
        <ul className="space-y-2">
          <li className="flex items-center"><Check className="w-4 h-4 text-yellow-500 mr-2" />Modern Design</li>
          <li className="flex items-center"><Check className="w-4 h-4 text-yellow-500 mr-2" />News & Updates</li>
          <li className="flex items-center"><Check className="w-4 h-4 text-yellow-500 mr-2" />Event Calendar</li>
        </ul>
      </motion.div>

      <motion.div
        className="bg-gray-900 p-6 rounded-lg"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.2 }}
      >
        <BookOpen className="w-8 h-8 text-yellow-500 mb-4" />
        <h4 className="text-lg font-bold mb-2">Management System</h4>
        <ul className="space-y-2">
          <li className="flex items-center"><Check className="w-4 h-4 text-yellow-500 mr-2" />Attendance Tracking</li>
          <li className="flex items-center"><Check className="w-4 h-4 text-yellow-500 mr-2" />Staff Management</li>
          <li className="flex items-center"><Check className="w-4 h-4 text-yellow-500 mr-2" />Resource Planning</li>
        </ul>
      </motion.div>

      <motion.div
        className="bg-gray-900 p-6 rounded-lg"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.3 }}
      >
        <Phone className="w-8 h-8 text-yellow-500 mb-4" />
        <h4 className="text-lg font-bold mb-2">Free Add-ons</h4>
        <ul className="space-y-2">
          <li className="flex items-center"><Check className="w-4 h-4 text-yellow-500 mr-2" />Holiday Updates</li>
          <li className="flex items-center"><Check className="w-4 h-4 text-yellow-500 mr-2" />Leave Management</li>
          <li className="flex items-center"><Check className="w-4 h-4 text-yellow-500 mr-2" />Basic Reports</li>
        </ul>
      </motion.div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white text-white">
      {/* Previous sections remain the same */}

      {/* Package System Section */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold mb-6">Complete Solution Packages</h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Choose the perfect package that combines attendance system, school website, 
              management software, and mobile apps all in one integrated solution.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {packages.map((pkg, index) => (
              <motion.div
                key={index}
                className={`relative bg-white rounded-xl p-8 border-2 ${
                  pkg.highlight ? 'border-yellow-500' : 'border-gray-800'
                }`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                {pkg.highlight && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-yellow-500 text-black px-4 py-1 rounded-full text-sm font-bold">
                      Most Popular
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-center mb-6">
                  {pkg.icon}
                </div>
                <h3 className="text-2xl font-bold text-center mb-4">{pkg.title}</h3>
                <p className="text-gray-400 text-center mb-6">{pkg.description}</p>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="text-lg font-semibold mb-3 text-yellow-500">Includes</h4>
                    <ul className="space-y-3">
                      {pkg.mainFeatures.map((feature, fIndex) => (
                        <li key={fIndex} className="flex items-center">
                          <CheckCircle className="w-5 h-5 text-yellow-500 mr-2 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="text-lg font-semibold mb-3 text-yellow-500">Features</h4>
                    <ul className="space-y-3">
                      {pkg.includedFeatures.map((feature, fIndex) => (
                        <li key={fIndex} className="flex items-start">
                          <Check className="w-5 h-5 text-yellow-500 mr-2 mt-1 flex-shrink-0" />
                          <span className="text-gray-300">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <button
                onClick={() => navigate('/contact')}
                className={`w-full mt-8 py-3 px-6 rounded-lg font-bold transition-colors ${
                  pkg.highlight 
                    ? 'bg-yellow-500 text-black hover:bg-blue-600' 
                    : 'bg-gray-800 text-white hover:bg-gray-700'
                }`}>
                  Request Quote
                </button>
              </motion.div>
            ))}
          </div>

          <div className="mt-20">
            <h3 className="text-2xl font-bold text-center mb-8">Included App Features</h3>
            <AppFeatures />
          </div>
        </div>
      </section>

      {/* Previous sections remain the same */}
    </div>
  );
};

export default ProductShowcase;